/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';

import moment from 'moment';

import Modal from '../../Modal';
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { formatDateBr } from '../../../utils/formatDate';
import api from '../../../services/api';

import { useForm } from 'react-hook-form';
import { MuiDatePicker } from '../../Material/MuiDatePicker';

import { IFilaData } from '../../../pages/PainelAgendamentos';
import { OperationsReport } from '../../Reports/OperationsReport';

interface IModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: string | boolean) => string;
}

const columns: Column[] = [
  {
    id: 'data',
    label: 'Data',
    minWidth: 100,
    align: 'center',
    format: (value: string | boolean) => formatDateBr(String(value)),
  },
  { id: 'motorista', label: 'Motorista', minWidth: 170, align: 'center' },
  {
    id: 'cpf',
    label: 'CPF',
    minWidth: 140,
    align: 'center',
  },
  {
    id: 'placa',
    label: 'Placa',
    minWidth: 100,
    align: 'center',
  },
  { id: 'filaid', label: 'Fila', minWidth: 100, align: 'center' },
  { id: 'agendamentoid', label: 'Agendamento', minWidth: 100, align: 'center' },

  {
    id: 'volumecarregado',
    label: 'Volume',
    minWidth: 140,
    align: 'center',
  },
];

interface IFormData {
  queryDate: string;
}

const ModalOperationsReport = React.forwardRef<HTMLInputElement, IModalProps>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ isOpen, setIsOpen }, ref) => {
    const [loading, setLoading] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);

    const [reportData, setReportData] = useState<any>(null);

    const [filaData, setFilaData] = useState<IFilaData[] | null>(null);

    const minWidthMuiInput = '400px';
    const maxWidthMuiInput = '400px';

    const { control, watch, reset } = useForm<IFormData>({
      defaultValues: {
        queryDate: new Date().toISOString(),
      },
    });

    const queryDate = watch('queryDate');

    useEffect(() => {
      if (!isOpen || !queryDate) {
        return;
      }

      const loadCarregamentosFinalizados = async () => {
        try {
          setLoading(true);

          const formattedDate = moment(queryDate).format('YYYY-MM-DD');

          const response = await api.get(
            `/carregamentos/finalizados/bydate/${formattedDate}`,
          );

          setFilaData(response.data);
          setLoading(false);
        } catch (error) {
          console.error('Error loading checklist:', error);
          setFilaData([]);
          setLoading(false);
        }
      };

      loadCarregamentosFinalizados();
    }, [isOpen, queryDate]);

    useEffect(() => {
      if (!reportData) return;

      // Wait until React renders EtanolChecklistReport into the DOM.
      const printTimer = window.setTimeout(() => {
        window.print();
      }, 100);

      return () => {
        window.clearTimeout(printTimer);
      };
    }, [reportData]);

    useEffect(() => {
      const handleAfterPrint = () => {
        setReportData(null);
      };

      window.addEventListener('afterprint', handleAfterPrint);

      return () => {
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }, []);

    const handleLoadReportData = async (fila: IFilaData) => {
      setLoadingReport(true);

      const response = await api.get(`/carregamentos/operationsreport`, {
        params: {
          date: queryDate,
          produto: fila.produto,
          placa: fila.placa,
          placa2: fila.placa2 ?? null,
        },
      });

      setReportData(response.data);
      setLoadingReport(false);
    };

    const handleCloseModal = () => {
      setFilaData(null);
      setLoadingReport(false);
      reset();
    };

    return (
      <>
        <Modal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          size="1200px"
          onCloseModal={() => handleCloseModal()}
        >
          <div style={{ position: 'relative' }}>
            {loadingReport && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  borderRadius: '4px',
                }}
              >
                <CircularProgress sx={{ color: '#00a859' }} />

                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#171412',
                  }}
                >
                  Gerando relatório...
                </span>
              </div>
            )}

            <h1
              style={{
                color: '#00a859',
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              Relatório Operação
            </h1>

            <h6
              style={{
                textAlign: 'center',
                marginBottom: '20px',
                opacity: 0.7,
              }}
            >
              Clique em um carregamento da tabela abaixo para gerar o relatório
            </h6>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                padding: '1rem 0',
              }}
            >
              <MuiDatePicker
                name={'queryDate'}
                label={'Selecione a data'}
                control={control}
                minWidth={minWidthMuiInput}
                maxWidth={maxWidthMuiInput}
                returnFormat="YYYY-MM-DD"
              />
            </div>

            <Paper sx={{ width: '100%', overflow: 'auto' }}>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '80px',
                  }}
                >
                  <CircularProgress sx={{ color: '#00a859' }} />
                </div>
              ) : (
                <TableContainer sx={{ maxHeight: 640, padding: 2 }}>
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow>
                        {columns.map(column => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{
                              // top: 57,
                              minWidth: column.minWidth,
                            }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filaData && filaData.length > 0 ? (
                        filaData.map(fila => {
                          return (
                            <TableRow
                              key={fila.agendamentoid}
                              sx={{
                                cursor: 'pointer',
                              }}
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              onClick={() => {
                                handleLoadReportData(fila);
                              }}
                            >
                              {columns.map(column => {
                                const value = fila[column.id];

                                return (
                                  <TableCell
                                    key={column.id}
                                    align={column.align}
                                  >
                                    {column.format
                                      ? column.format(value)
                                      : value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            Nenhum carregamento encontrado.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </div>
        </Modal>

        {reportData && (
          <OperationsReport reportData={reportData} date={queryDate} />
        )}
      </>
    );
  },
);

ModalOperationsReport.displayName = 'ModalOperationsReport';

export default ModalOperationsReport;
